// Base URL of the Lovable (InsureHub) app rendered inside the iframe.
export const DEFAULT_INSUREHUB_URL = (
  import.meta.env.VITE_APP_INSUREHUB_URL || "https://connect-sales-stage.lovable.app"
).trim();

export const ROUTE_LABELS: Record<string, string> = {
  insurehub: "Commission Dashboard",
  ledger: "Commission Ledger",
  invoices: "Invoices",
  reconciliation: "Reconciliation Workspace",
  variances: "Variances & Disputes",
  tds: "TDS",
  reports: "Reports",
  irdai: "IRDAI Compliance",
  config: "Configuration",
  cases: "Cases",
  claims: "Claims",
};

export const getRouteLabel = (pathname: string) => {
  const segment = pathname.split("/").filter(Boolean)[0] ?? "";
  if (ROUTE_LABELS[segment]) return ROUTE_LABELS[segment];
  if (!segment) return "";
  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

// Broker Operations pages live under /insurehub/* in the Lovable app, unlike the
// CRM pages (cases, claims) and the dashboard (/insurehub) which sit at the root.
// Only the embed-aware layout under this prefix honours ?embed=1 to hide the sidebar.
const INSUREHUB_PREFIXED_SEGMENTS = new Set([
  "ledger",
  "invoices",
  "reconciliation",
  "variances",
  "tds",
  "reports",
  "irdai",
  "config",
]);

const withLovablePrefix = (pathname: string) => {
  const segment = pathname.split("/").filter(Boolean)[0] ?? "";
  return INSUREHUB_PREFIXED_SEGMENTS.has(segment) ? `/insurehub${pathname}` : pathname;
};

// The Lovable app's own internal route for each module, keyed by our backend module_name (a
// stable convention we control) rather than the backend's module.path (which admins can rename
// freely and is not guaranteed to match Lovable's segment names, e.g. "irdai-compliance" here vs
// "/insurehub/irdai" inside the Lovable app).
export const MODULE_LOVABLE_BASE_PATH: Record<string, string> = {
  "commission-dashboard": "/insurehub",
  "commission-ledger": "/insurehub/ledger",
  invoices: "/insurehub/invoices",
  reconciliation: "/insurehub/reconciliation",
  variances: "/insurehub/variances",
  tds: "/insurehub/tds",
  reports: "/insurehub/reports",
  "irdai-compliance": "/insurehub/irdai",
  configuration: "/insurehub/config",
  case: "/cases",
  claims: "/claims",
};

export const buildIframeSrc = (
  pathname: string,
  search: string,
  baseUrl?: string,
  moduleName?: string,
  modulePath?: string
) => {
  const base = (baseUrl || DEFAULT_INSUREHUB_URL).replace(/\/+$/, "");
  const lovableBasePath = moduleName ? MODULE_LOVABLE_BASE_PATH[moduleName] : undefined;

  // Nested sub-routes (e.g. "/commission-ledger/invoice/123") forward whatever comes after the
  // module's own base path straight through to the same position in the Lovable app's route.
  const nestedSuffix =
    lovableBasePath !== undefined && modulePath && pathname.startsWith(modulePath)
      ? pathname.slice(modulePath.length)
      : "";

  const path =
    lovableBasePath !== undefined
      ? `${lovableBasePath}${nestedSuffix}`
      : withLovablePrefix(pathname);
  const separator = search ? "&" : "?";
  return `${base}${path}${search}${separator}embed=1`;
};
